import { mrtCaseSchema } from "@/components/Forms/schema/mrtCaseSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createDataSelector, createResourceCollectionSelector } from "jsonapi-zod-query";
import type {
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_SortingState,
} from "material-react-table";

const resourceSelector = createResourceCollectionSelector({
    type: "case",
    attributesSchema: mrtCaseSchema,
});

const companionSelector = createDataSelector(resourceSelector);

export type Cases = ReturnType<typeof companionSelector>;
export type MrtCase = Cases[number];

type Props = {
    personUUID: string;
    columnFilters: MRT_ColumnFiltersState;
    globalFilter: string;
    pagination: MRT_PaginationState;
    sorting: MRT_SortingState;
};

export const useFetchMrtCasesForPerson = ({
    personUUID,
    columnFilters,
    globalFilter,
    pagination,
    sorting,
}: Props): UseQueryResult<Cases> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: [
            "person-cases-table-data",
            personUUID,
            columnFilters,
            globalFilter,
            pagination.pageIndex,
            pagination.pageSize,
            sorting,
        ],
        queryFn: async () => {
            const fetchURL = apiUrl("/person/cases/for-person");

            const response = await fetch(fetchURL.href, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    personUUID: personUUID,
                    start: pagination.pageIndex * pagination.pageSize,
                    size: pagination.pageSize,
                    filters: columnFilters ?? [],
                    globalFilter: globalFilter ?? "",
                    sorting: sorting ?? [],
                }),
            });

            return await response.json();
        },
        select: companionSelector,
    });
};
